import { useState, useCallback, useRef } from 'react';
import api from '../../services/api';
import { Goal } from './types';

export const useGoalTree = (loadData: () => Promise<any>, goals: Goal[]) => {
  const [showTreeConnectModal, setShowTreeConnectModal] = useState(false);
  const [treeConnectSource, setTreeConnectSource] = useState<number | null>(null);
  const [treeConnectPosition, setTreeConnectPosition] = useState<{ x: number; y: number } | null>(null);
  const connectingNodeId = useRef<number | null>(null);

  // Проверка на циклические зависимости
  const isDescendant = useCallback((parentId: number, childId: number): boolean => {
    const parent = goals.find(g => g.id === parentId);
    if (!parent) return false;
    if (parent.parentGoalId === childId) return true;
    if (parent.parentGoalId) return isDescendant(parent.parentGoalId, childId);
    return false;
  }, [goals]);

  const onConnectNodes = useCallback(async (connection: any) => {
    const sourceGoalId = Number(connection.source);
    const targetGoalId = connection.target ? Number(connection.target) : null;
    
    if (targetGoalId) {
      if (sourceGoalId === targetGoalId) {
        alert('❌ Нельзя связать цель саму с собой!');
        return;
      }
      
      // Проверка на циклическую зависимость
      // sourceGoalId будет родителем для targetGoalId
      // Проверяем, не является ли sourceGoalId потомком targetGoalId
      if (isDescendant(sourceGoalId, targetGoalId)) {
        alert('❌ Нельзя создать циклическую зависимость! Вы пытаетесь привязать родительскую цель к своему потомку.');
        return;
      }
      
      try {
        await api.put(`/api/goals/${targetGoalId}`, {
          parentGoalId: sourceGoalId
        });
        
        await loadData();
      } catch (error) {
        console.error('Ошибка при связывании целей:', error);
        alert('❌ Ошибка при связывании целей');
      }
    } else {
      setTreeConnectSource(sourceGoalId);
      setShowTreeConnectModal(true);
    }
  }, [loadData, isDescendant]);

  const handleTreeConnectGoal = async (targetGoalId: number, allGoals: Goal[], projectId?: number) => {
    if (treeConnectSource === null) return;
    
    // Если treeConnectSource === -1, значит тянули от проекта
    if (treeConnectSource === -1) {
      if (!projectId) {
        alert('❌ Ошибка: не указан ID проекта');
        return;
      }
      
      // Проверяем, есть ли у цели дочерние цели
      const targetGoal = allGoals.find(g => g.id === targetGoalId);
      const childrenCount = allGoals.filter(g => g.parentGoalId === targetGoalId).length;
      
      if (childrenCount > 0) {
        const confirmed = window.confirm(
          `⚠️ У цели "${targetGoal?.name}" есть ${childrenCount} подцелей.\n\n` +
          `Все подцели останутся привязанными к этой цели и переместятся вместе с ней в дерево проекта.\n\n` +
          `Продолжить?`
        );
        
        if (!confirmed) {
          setShowTreeConnectModal(false);
          setTreeConnectSource(null);
          setTreeConnectPosition(null);
          return;
        }
      }
      
      try {
        // Привязываем цель к проекту (делаем её корневой)
        // Все дочерние цели остаются привязанными через parentGoalId
        await api.put(`/api/goals/${targetGoalId}`, {
          projectId: projectId,
          parentGoalId: null
        });
        
        setShowTreeConnectModal(false);
        setTreeConnectSource(null);
        setTreeConnectPosition(null);
        await loadData();
      } catch (error) {
        console.error('Ошибка при привязке цели к проекту:', error);
        alert('❌ Ошибка при привязке цели к проекту');
      }
      return;
    }
    
    // Обычная привязка цели к родительской цели
    if (treeConnectSource === targetGoalId) {
      alert('❌ Нельзя связать цель саму с собой!');
      return;
    }
    
    // Проверка на циклическую зависимость
    if (isDescendant(treeConnectSource, targetGoalId)) {
      alert('❌ Нельзя создать циклическую зависимость! Вы пытаетесь привязать родительскую цель к своему потомку.');
      return;
    }
    
    // Проверяем, есть ли у цели дочерние цели
    const targetGoal = allGoals.find(g => g.id === targetGoalId);
    const childrenCount = allGoals.filter(g => g.parentGoalId === targetGoalId).length;
    
    if (childrenCount > 0) {
      const confirmed = window.confirm(
        `⚠️ У цели "${targetGoal?.name}" есть ${childrenCount} подцелей.\n\n` +
        `Все подцели останутся привязанными к этой цели и переместятся вместе с ней.\n\n` +
        `Продолжить?`
      );
      
      if (!confirmed) {
        setShowTreeConnectModal(false);
        setTreeConnectSource(null);
        setTreeConnectPosition(null);
        return;
      }
    }
    
    try {
      await api.put(`/api/goals/${targetGoalId}`, {
        parentGoalId: treeConnectSource
      });
      
      setShowTreeConnectModal(false);
      setTreeConnectSource(null);
      setTreeConnectPosition(null);
      await loadData();
    } catch (error) {
      console.error('Ошибка при связывании целей:', error);
      alert('❌ Ошибка при связывании целей');
    }
  };

  return {
    showTreeConnectModal,
    setShowTreeConnectModal,
    treeConnectSource,
    setTreeConnectSource,
    treeConnectPosition,
    setTreeConnectPosition,
    connectingNodeId,
    onConnectNodes,
    handleTreeConnectGoal
  };
};
